<template>
  <div>
    <div class="schedule-header">
      <div class="schedule-header-title">
        <h1>Schedule</h1>
        <button
          v-if="userCanCreateReservations"
          class="button-primary"
          @click="openReservationModal"
        >
          Add Event
          <img src="/static/img/plus-white.svg" alt="" />
        </button>
      </div>
      <input type="date" v-model="selectedDate" style="z-index: 10" />
      <HiloSelect
        v-model="filter.selectedEventType"
        :options="eventTypes"
        searchable
        placeholder="Type"
        style="z-index: 10"
      />
      <HiloSelect
        v-model="filter.selectedScheduleType"
        :options="scheduleTypes"
        searchable
        placeholder="Full Schedule"
        style="z-index: 10"
      />
    </div>
    <div
      :class="`table-responsive ${tableData.length && 'tbl'}`"
      :style="`overflow: ${tableData.length > 0 ? 'auto' : 'hidden'}`"
    >
      <table class="table table-bordered">
        <thead>
          <tr>
            <th></th>
            <th class="text-left">Tail Number</th>
            <th class="text-left">Type</th>
            <th
              v-for="hour in 24"
              :key="hour"
              :style="{ minWidth: hourWidth + 'px', maxWidth: '60px' }"
            >
              {{ String(hour - 1).padStart(2, "0") }}:00
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in tableData" :key="index">
            <td>
              <center>
                <div :style="'background: ' + getColor(row)"></div>
              </center>
            </td>
            <td class="text-left">{{ row.tailNumber || row.name }}</td>
            <td class="text-left">{{ row.model }}</td>
            <td v-for="hour in 24" :key="hour" class="position-relative">
              <div v-for="event in getEventsForRow(row)" :key="event.id">
                <div
                  class="position-absolute rounded p-1 small event-text"
                  v-if="isEventVisible(event, hour - 1)"
                  :style="{
                    backgroundColor: event.eventType.scheduledColor,
                    color: event.eventType.textColor,
                    height: '100%',
                    width: getEventWidth(event, hour - 1) + 'px',
                  }"
                  @click="handleEventClick(event, $event)"
                >
                  {{ event.displayName }}
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div
        class="w-full text-center font-italic text-secondary mt-3 pb-3"
        v-if="tableData.length < 1"
      >
        No data
      </div>
    </div>
  </div>
</template>

<script>
import HiloSelect from "@/components/HiloComponents/HiloSelect.vue";
import dayjs from "dayjs";

export default {
  components: { HiloSelect },
  props: {
    currentUser: Object,
    aircraft: Array,
    instructors: Array,
    rooms: Array,
    eventTypes: Array,
    selectedCalendarDate: Object,
    userCanCreateReservations: Boolean,
    userCanAccessSettings: Boolean,
    standbyFlightsExist: Boolean,
    events: Array,
    filteredAircraft: Array,
    filteredInstructors: Array,
    filteredRooms: Array,
  },
  data() {
    return {
      selectedDate: dayjs().format("YYYY-MM-DD"),
      hourWidth: 60,
      selectedType: null,
      filter: {
        selectedEventType: "all",
        selectedScheduleType: "all",
      },
      scheduleTypes: [
        { id: "all", name: "Full Schedule" },
        { id: "Standby Flights", name: "Standby Flights" },
        { id: "Flight Schedule", name: "Flight Schedule" },
        { id: "Grounds Schedule", name: "Grounds Schedule" },
        // { id: 'scheduled', name: 'Student Solos' },
        { id: "MX Schedule", name: "MX Schedule" },
      ],
    };
  },
  watch: {
    "filter.selectedScheduleType"(val) {
      if (!val) this.filter.selectedScheduleType = "all";
      if (val !== "Grounds Schedule") {
        [
          "update-aircraft-filter",
          "update-instructor-filter",
          "update-room-filter",
        ].forEach((event) => {
          this.$emit(event, this.filter.selectedScheduleType);
        });
      }
    },
    selectedDate() {
      this.emitDateChange();
    },
  },
  computed: {
    tableData() {
      return this.filter.selectedScheduleType === "Grounds Schedule"
        ? this.filteredRooms
        : [
            ...this.filteredAircraft,
            ...this.filteredInstructors,
            ...this.filteredRooms,
          ];
    },
    filteredEvents() {
      if (
        this.filter.selectedEventType !== "all" &&
        this.filter.selectedEventType !== null
      ) {
        return this.events.filter((event) => {
          return event.eventTypeId === this.filter.selectedEventType;
        });
      }
      return this.events;
    },
  },
  methods: {
    getColor(status) {
      if (!status.tailNumber || !status.aircraftStatusId) return "transparent";

      return status.aircraftStatus === "Active"
        ? "green"
        : status.aircraftStatus === "Inspection Due Soon"
          ? "orange"
          : "red";
    },
    handleEventClick(event, e) {
      const plane = this.findData(this.aircraft, event, "aircraftId");
      const instructor = this.findData(
        this.filteredInstructors,
        event,
        "instructorId",
      );
      const room = this.findData(this.filteredRooms, event, "roomId");
      this.$emit("show-modal", event, plane, instructor, room);
    },
    findData(arr, event, key) {
      return arr.find((item) => item.id === event[key]);
    },
    emitDateChange() {
      this.$emit("update-date", this.selectedDate);
    },
    openReservationModal() {
      this.$emit("show-reservation-modal");
    },
    getEventsForRow(row) {
      return this.filteredEvents
        .filter((event) => {
          if (event.aircraft && row.tailNumber) {
            return event.aircraft.id === row.id;
          }
          if (event.instructor && row.name) {
            return event.instructor.id === row.id;
          }
          if (event.occupants && row.model) {
            return event.occupants.id === row.id;
          }
          return false;
        })
        .slice(0, 1);
    },
    isEventVisible(event, hour) {
      const eventStart = dayjs(event.startDateTime);
      const eventEnd = dayjs(event.endDateTime);
      const selectedDate = dayjs(this.selectedDate);

      if (
        selectedDate.isBefore(eventStart, "day") ||
        selectedDate.isAfter(eventEnd, "day")
      ) {
        return false;
      }

      if (selectedDate.isSame(eventStart, "day")) {
        return hour === eventStart.hour();
      }

      if (
        (selectedDate.isAfter(eventStart, "day") &&
          selectedDate.isBefore(eventEnd, "day")) ||
        selectedDate.isSame(eventEnd, "day")
      ) {
        return hour === 0;
      }

      return false;
    },
    getEventWidth(event) {
      const eventStart = dayjs(event.startDateTime);
      const eventEnd = dayjs(event.endDateTime);
      const selectedDate = dayjs(this.selectedDate).startOf("day");

      let startHour = eventStart.hour();
      let endHour = eventEnd.hour() + (eventEnd.minute() > 0 ? 1 : 0);

      if (!selectedDate.isSame(eventStart, "day")) {
        startHour = 0;
      }

      if (!selectedDate.isSame(eventEnd, "day")) {
        endHour = 24;
      }

      return (endHour - startHour) * 60;
    },
  },
};
</script>

<style scoped>
.schedule-container {
  display: flex;
  flex-direction: column;
}

.schedule-header {
  display: grid;
  align-items: center;
  gap: 15px;
  grid-template-columns: repeat(4, 1fr);
  box-sizing: border-box;
}

.button-primary {
  background-color: #3c78ff;
  color: #fff;
  border-radius: 5px;
  padding: 3px 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: normal;
  box-sizing: border-box;
  height: fit-content;
  font-size: 16px;
}

.schedule-header-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.schedule-header-title h1 {
  font-weight: 600;
  color: #404040;
}

.table-responsive {
  /* overflow-x: auto; */
  white-space: nowrap;
  overflow-y: hidden;
  position: relative;
  max-width: 100vw;
  border-top-left-radius: 15px;
  border-top-right-radius: 15px;
  background: #fff;
}

.tbl::before {
  content: "";
  width: 2px;
  height: 100%;
  background: #a0a0a0;
  position: absolute;
  margin-left: 330px;
  z-index: 1;
}

.table-bordered {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;
}

th:nth-child(1),
th:nth-child(2),
th:nth-child(3) {
  background: #3c78ff;
  color: #fff;
}

td:nth-child(1),
td:nth-child(2),
td:nth-child(3) {
  font-size: 16px !important;
}

th:nth-child(1),
td:nth-child(1) {
  min-width: 30px;
  max-width: 30px;
  width: 30px;
  z-index: 2;
  position: sticky;
  left: 0px;
}

th:nth-child(2),
td:nth-child(2) {
  min-width: 150px;
  max-width: 150px;
  width: 150px;
  z-index: 2;
  position: sticky;
  left: 30px;
}

th:nth-child(3),
td:nth-child(3) {
  min-width: 150px;
  max-width: 150px;
  width: 150px;
  z-index: 2;
  position: sticky;
  left: 180px;
}

th,
td {
  padding: 3px !important;
  text-align: center;
}
td {
  background: #fff;
}

th:nth-child(n + 4),
td:nth-child(n + 4) {
  min-width: 60px !important;
  max-width: 60px !important;
  width: 60px !important;
}

th {
  background: #3c78ff;
  color: white !important;
  text-transform: capitalize !important;
  font-size: 16px !important;
}

.position-absolute {
  top: 0;
  left: 0;
  border-radius: 30px !important;
  text-overflow: ellipsis;
  z-index: 1;
}

.schedule-header-filter {
  display: grid;
}

td:first-child div {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  /* background: gray; */
}

th:first-child {
  border-top-left-radius: 15px;
}

.event-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  font-size: 13px;
}
input[type="date"] {
  background: #fff;
  border: 1px solid #ccc;
  height: 33px;
}
</style>
